import { useEffect, useState } from 'react';

/**
 * The five themeable chart series colours, read from the `--chart-1..5` CSS
 * custom properties (see `src/index.css` and `src/lib/theme.ts`).
 */
export interface ChartColors {
    chart1: string;
    chart2: string;
    chart3: string;
    chart4: string;
    chart5: string;
}

const CHART_VARS = ['--chart-1', '--chart-2', '--chart-3', '--chart-4', '--chart-5'] as const;

const FALLBACK: ChartColors = {
    chart1: '#2563eb',
    chart2: '#0ea5e9',
    chart3: '#8b5cf6',
    chart4: '#f59e0b',
    chart5: '#ef4444',
};

function readChartColors(): ChartColors {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
        return FALLBACK;
    }

    const styles = window.getComputedStyle(document.documentElement);
    const [chart1, chart2, chart3, chart4, chart5] = CHART_VARS.map(name =>
        styles.getPropertyValue(name).trim()
    );

    return {
        chart1: chart1 || FALLBACK.chart1,
        chart2: chart2 || FALLBACK.chart2,
        chart3: chart3 || FALLBACK.chart3,
        chart4: chart4 || FALLBACK.chart4,
        chart5: chart5 || FALLBACK.chart5,
    };
}

/**
 * Reads the `--chart-1..5` design tokens at runtime instead of hard-coding hex
 * values, so chart series follow the active tenant brand colour (applied at
 * runtime via `applyTenantTheme`, see `src/lib/theme.ts`) and the light/dark
 * colour mode.
 *
 * Re-reads whenever `document.documentElement`'s `class` (colour mode toggle)
 * or `style` (runtime tenant theme override) attribute changes, via a
 * `MutationObserver` rather than a resize/interval poll.
 */
export function useChartColors(): ChartColors {
    const [colors, setColors] = useState<ChartColors>(() => readChartColors());

    useEffect(() => {
        if (typeof document === 'undefined') return;

        const update = () => setColors(readChartColors());
        update();

        const observer = new MutationObserver(update);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class', 'style'],
        });

        return () => observer.disconnect();
    }, []);

    return colors;
}

/** Convenience accessor for code that wants to cycle through the ramp by index. */
export function chartColorList(colors: ChartColors): string[] {
    return [colors.chart1, colors.chart2, colors.chart3, colors.chart4, colors.chart5];
}

export default useChartColors;
